import type { Optional } from 'utility-types'

import { createArrayComparator, createNamedEqualityChecker, type ReadonlyDate } from '../../utils'

import type { ConnectwareError, CybusRole, MFAStatus } from '..'
import { type FieldValidation, isFieldValidated } from '../Field'
import type { Loadable } from '../Loadable'
import { arePermissionArrayEquals, type CybusPersistedPermission, type EditableCybusPermissionInheritance, isPermissionValid } from '.'
import type { PermissionedEntity, PermissionedForm } from './Base'

export enum CybusUserProvider {
    CONNECTWARE = 'CONNECTWARE',
    LDAP = 'LDAP',
    MICROSOFT_ENTRA_ID = 'MICROSOFT_ENTRA_ID',
}

export enum CybusUserAuthenticationMethod {
    CERTIFICATE = 'CERTIFICATE',
    PASSWORD = 'PASSWORD',
    TOKEN = 'TOKEN',
}

export type CybusUser =
    & PermissionedEntity
    & Readonly<{
        /**
         * Unique
         */
        username: string

        /**
         * The MFA status of the user
         */
        mfaStatus: MFAStatus

        /**
         * Nullable value mean mfa is not setup on the system
         */
        isMfaEnforced: boolean | null

        /**
         * The roles the user has
         */
        roles: readonly CybusRole['name'][]

        /**
         * All permissions of the user
         */
        allPermissions: readonly CybusPersistedPermission[]

        /**
         * The methods through which the user can authenticate
         */
        authenticationMethods: readonly CybusUserAuthenticationMethod[]

        /**
         * When was the user last seen
         *
         * If null, then the information is not available
         */
        lastSeen: ReadonlyDate | null

        /**
         * The source of the user
         */
        provider: CybusUserProvider

        mqttPublishPrefix: string | null

        /**
         * Indicate if the entity can have their MFA updated
         */
        canUpdateMFA: boolean
    }>

type FullUserForm =
    & Pick<
        CybusUser,
        | 'authenticationMethods'
        | 'id'
        | 'mqttPublishPrefix'
        | 'username'
        | 'mfaStatus'
        | 'isMfaEnforced'
        | 'canEditName'
        | 'canEditOthers'
        | 'canUpdateMFA'
        | 'canDelete'
    >
    & PermissionedForm
    & Readonly<{
        password: string | null
        passwordConfirmation: string | null

        passwordValidation: FieldValidation
        usernameValidation: FieldValidation
        /**
         * @todo replace this with FieldValidation
         * @see {FieldValidation}
         */
        mqttPublishPrefixValidation: ConnectwareError | null

        roles: readonly CybusRole[]
        rolesPermissions: readonly EditableCybusPermissionInheritance[]

        roleInput: string | null
        allRoles: Loadable<readonly CybusRole[]>
    }>

export type UserCreationForm = Pick<
    FullUserForm,
    | 'allRoles'
    | 'authenticationMethods'
    | 'mqttPublishPrefix'
    | 'mqttPublishPrefixValidation'
    | 'password'
    | 'passwordConfirmation'
    | 'passwordValidation'
    | 'permissions'
    | 'roleInput'
    | 'roles'
    | 'rolesPermissions'
    | 'selectedTemplate'
    | 'templateInput'
    | 'templates'
    | 'username'
    | 'usernameValidation'
    | 'isMfaEnforced'
    | 'canEditName'
    | 'canEditOthers'
    | 'canUpdateMFA'
    | 'canDelete'
>

export type UserCreationRequest = Pick<
    UserCreationForm,
    'authenticationMethods' | 'mqttPublishPrefix' | 'password' | 'permissions' | 'roles' | 'username' | 'isMfaEnforced'
>

export type UserEditingForm = Pick<
    FullUserForm,
    | 'allRoles'
    | 'authenticationMethods'
    | 'id'
    | 'mqttPublishPrefix'
    | 'mqttPublishPrefixValidation'
    | 'password'
    | 'passwordConfirmation'
    | 'passwordValidation'
    | 'mfaStatus'
    | 'isMfaEnforced'
    | 'permissions'
    | 'roleInput'
    | 'roles'
    | 'rolesPermissions'
    | 'selectedTemplate'
    | 'templateInput'
    | 'templates'
    | 'username'
    | 'usernameValidation'
    | 'canEditName'
    | 'canEditOthers'
    | 'canUpdateMFA'
    | 'canDelete'
>
export type UserEditingRequest = Optional<
    Pick<
        UserEditingForm,
        'id' | 'authenticationMethods' | 'mqttPublishPrefix' | 'password' | 'permissions' | 'roles' | 'username' | 'mfaStatus' | 'isMfaEnforced'
    >,
    'authenticationMethods' | 'mqttPublishPrefix' | 'password' | 'permissions' | 'roles' | 'username' | 'mfaStatus' | 'isMfaEnforced'
>

export const areUserCreationFormsEqual = createNamedEqualityChecker<
    Pick<
        UserCreationForm,
        'username' | 'mqttPublishPrefix' | 'password' | 'passwordConfirmation' | 'roles' | 'permissions' | 'authenticationMethods' | 'isMfaEnforced'
    >
>(
    {
        username: null,
        mqttPublishPrefix: null,
        password: null,
        passwordConfirmation: null,
        roles: createArrayComparator(),
        permissions: arePermissionArrayEquals,
        authenticationMethods: createArrayComparator(),
        isMfaEnforced: null,
    },
    'areUserFormsEqual'
)

export const areUserEditingFormsEqual = createNamedEqualityChecker<
    Pick<
        UserEditingForm,
        | 'username'
        | 'mqttPublishPrefix'
        | 'password'
        | 'passwordConfirmation'
        | 'roles'
        | 'permissions'
        | 'authenticationMethods'
        | 'mfaStatus'
        | 'isMfaEnforced'
    >
>(
    {
        username: null,
        mfaStatus: null,
        mqttPublishPrefix: null,
        password: null,
        passwordConfirmation: null,
        roles: createArrayComparator(),
        permissions: arePermissionArrayEquals,
        authenticationMethods: createArrayComparator(),
        isMfaEnforced: null,
    },
    'areUserFormsEqual'
)

/**
 * Checks if username is present and if validation is positive
 */
const isUsernameValid = ({ username, usernameValidation }: Pick<UserCreationForm, 'username' | 'usernameValidation'>): boolean =>
    Boolean(username) && isFieldValidated(usernameValidation)

const arePermissionsValid = ({ permissions }: Pick<UserCreationForm, 'permissions'>): boolean => permissions.every(isPermissionValid)

/**
 * Checks if password is relevant or if validation is positive
 */
const isPasswordValid = ({ password, passwordValidation }: Pick<UserCreationForm, 'password' | 'passwordValidation'>): boolean =>
    (password === null && passwordValidation === null) || isFieldValidated(passwordValidation)

export const isUserCreatable = (form: UserCreationForm): boolean => isUsernameValid(form) && arePermissionsValid(form) && isPasswordValid(form)

export const isUserUpdatable = (form: UserEditingForm): boolean =>
    (!form.canEditName || isUsernameValid(form)) && arePermissionsValid(form) && isPasswordValid(form)

export const isUserRemovable = (u: UserEditingForm): boolean => Boolean(u.id) && u.canDelete
